import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { STOREFRONT } from '@/lib/utils/translation';

type CategoriesMegaMenuProps = {
  lang: 'en' | 'de';
};

const CategoriesMegaMenu = ({ lang }: CategoriesMegaMenuProps) => {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const columns = 3;
  const itemsPerColumn = Math.ceil(categories.length / columns);

  // Detect touch devices once on mount
  useEffect(() => {
    const handleTouchStart = () => setIsTouch(true);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);

  return (
    <div
      className="relative"
      role="button"
      tabIndex={0}
      onMouseEnter={() => !isTouch && setIsOpen(true)}
      onMouseLeave={() => !isTouch && setIsOpen(false)}
      onClick={() => setIsOpen((prev) => !prev)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsOpen((prev) => !prev);
        }
      }}
    >
      <button className="-ml-2 flex items-center gap-2 rounded-lg px-2 py-2 text-gray-900 hover:bg-gray-100 xl:ml-6 xl:px-4">
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="5" cy="5" r="1.5" />
          <circle cx="10" cy="5" r="1.5" />
          <circle cx="15" cy="5" r="1.5" />

          <circle cx="5" cy="10" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="15" cy="10" r="1.5" />

          <circle cx="5" cy="15" r="1.5" />
          <circle cx="10" cy="15" r="1.5" />
          <circle cx="15" cy="15" r="1.5" />
        </svg>
        <span className="font-bold">{t('common.category')}</span>
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, zIndex: -2 }}
          animate={{ opacity: 1, y: 0, zIndex: 2 }}
          exit={{ opacity: 0, y: -10, zIndex: -2 }}
          className="absolute left-6 z-50 mt-0 w-64 rounded-lg bg-white p-4 shadow-lg md:w-[40rem]"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[...Array(columns)].map((_, colIndex) => (
              <div key={colIndex} className="space-y-2">
                {categories
                  .slice(
                    colIndex * itemsPerColumn,
                    (colIndex + 1) * itemsPerColumn
                  )
                  .map((item) => (
                    <Link
                      key={item.id}
                      href={`/city/online-shop?tag_ids=${item.id}`}
                      className="block rounded-md px-3 py-2 font-medium text-gray-700 hover:bg-primary hover:bg-opacity-10"
                    >
                      {item.title?.[lang]}
                    </Link>
                  ))}
                {colIndex === columns - 1 && (
                  <Link
                    href="/city/online-shop"
                    className="block rounded-md px-3 py-2 font-bold text-primary hover:bg-primary hover:bg-opacity-10"
                  >
                    {t(`common.other_categories`)}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CategoriesMegaMenu;

type CategoryType = {
  id: number;
  order_index: number;
  title: {
    de: string;
    en: string;
  };
};

const categories: CategoryType[] = [
  {
    id: 203,
    order_index: 0,
    title: {
      de: 'Marktplatz',
      en: 'Marketplace',
    },
  },
  {
    id: 140,
    order_index: 1,
    title: {
      de: 'Fashion',
      en: 'Fashion',
    },
  },
  {
    id: 59,
    order_index: 2,
    title: {
      de: 'Food',
      en: 'Food stores',
    },
  },
  {
    id: 138,
    order_index: 2,
    title: {
      de: 'Beauty',
      en: 'Beauty',
    },
  },
  {
    id: 172,
    order_index: 2,
    title: {
      de: 'Reisen',
      en: 'Travel',
    },
  },
  {
    id: 165,
    order_index: 2,
    title: {
      de: 'Haus & Garten',
      en: 'Haus & Garten',
    },
  },
  {
    id: 187,
    order_index: 2,
    title: {
      de: 'Gesundheit',
      en: 'Health',
    },
  },
  {
    id: 194,
    order_index: 2,
    title: {
      de: '(Haus-)Tiere',
      en: 'Pets',
    },
  },
  {
    id: 176,
    order_index: 2,
    title: {
      de: 'Technik',
      en: 'Technology',
    },
  },
  {
    id: 136,
    order_index: 2,
    title: {
      de: 'Sport',
      en: 'Sport',
    },
  },
  {
    id: 214,
    order_index: 2,
    title: {
      de: 'Schmuck',
      en: 'Jewelry',
    },
  },
  // {
  //   id: 139,
  //   order_index: 2,
  //   title: {
  //     de: 'Kids',
  //     en: 'Kids',
  //   },
  // },
  // {
  //   id: 181,
  //   order_index: 2,
  //   title: {
  //     de: 'Bücher & Co',
  //     en: 'Bücher & Co',
  //   },
  // },
  // {
  //   id: 186,
  //   order_index: 2,
  //   title: {
  //     de: 'Geschenkideen',
  //     en: 'Geschenkideen',
  //   },
  // },
  // {
  //   id: 209,
  //   order_index: 2,
  //   title: {
  //     de: 'Freizeit',
  //     en: 'Freizeit',
  //   },
  // },
  // {
  //   id: 209,
  //   order_index: 2,
  //   title: {
  //     de: 'Alle Deals',
  //     en: 'Alle Deals',
  //   },
  // },
];
